import { acombaBridgeService } from '../../services/acombaBridgeService';
import type { AcombaInvoiceAr, AcombaPaymentLink } from '../../types/acombaBridge';

/**
 * How many ids go in one request to the bridge. The bridge caps a batch at 500 and rejects more, so this
 * stays comfortably under it: a server-side tightening should cost an extra round trip, not silently break
 * link resolution for every large company. Each request is one helper process, so bigger is cheaper —
 * this is the compromise, not a tuned number.
 */
const CHUNK_SIZE = 250;

/**
 * Resolves an ACCEO Transphere payment link for every invoice that could carry one.
 *
 * Shared by every extraction path so they cannot drift, and kept out of the store so it can be tested
 * directly rather than through a full extraction harness.
 *
 * Returns `null` for **not resolved** and `[]` for **resolved, none found**. The backend replaces each
 * relance's stored link set from this, so collapsing the two would let a brief Transphere outage delete
 * links an earlier sync resolved correctly.
 *
 * Best-effort throughout: nothing here may fail an extraction. A missing link costs a convenience; a
 * failed sync costs the sync.
 */
export async function resolvePaymentLinks(
    bridgeUrl: string,
    invoices: AcombaInvoiceAr[],
    getCancelled: () => boolean = () => false,
    transphereActive: boolean | null = null,
    reportReceiptSubscription: (active: boolean | null) => void = () => undefined,
): Promise<AcombaPaymentLink[] | null> {
    // Transphere is switched off for this société, so no invoice can have a link. That is a fact, not a
    // failure to look, so it is [] and not null — which lets the backend clear links left over from when
    // the subscription was still active. Only an explicit false counts: null means the bridge never told
    // us, and guessing "off" there would silently discard real links.
    if (transphereActive === false) return [];

    const ids = resolvableIds(invoices);

    // Nothing to resolve, but the société-wide receipt subscription rides on this call. Its outcome is
    // ignored: no resolvable invoice means no link exists, reachable Transphere or not.
    if (ids.length === 0) {
        if (!getCancelled()) await resolveChunk(bridgeUrl, [], reportReceiptSubscription);
        return [];
    }

    // Cancelled mid-extraction: nothing was asked, so nothing is known.
    if (getCancelled()) return null;

    const links: AcombaPaymentLink[] = [];

    for (let start = 0; start < ids.length; start += CHUNK_SIZE) {
        if (getCancelled()) return null;

        const chunk = await resolveChunk(bridgeUrl, ids.slice(start, start + CHUNK_SIZE), reportReceiptSubscription);

        // One failed chunk poisons the whole answer. A partial set is indistinguishable downstream from
        // "these are all the links there are", so the backend would replace the stored set with it and
        // delete every link belonging to a chunk we never got an answer for.
        if (chunk === null) return null;

        links.push(...chunk);
    }

    return links;
}

/**
 * The invoices worth asking about.
 *
 * Fully paid invoices are excluded: a relance is about money still owed, so a settled invoice can never
 * carry a payment link that anyone would use — and asking costs a Transphere round trip each. Deliberately
 * NOT filtered on `balance`, which the extraction leaves at zero for every invoice (the bridge computes
 * balances separately), so filtering on it would exclude everything.
 *
 * Invoices never transmitted to Transphere have no id and so no link; asking would be a certain miss.
 * De-duplicated because several records can share one Transphere id.
 */
function resolvableIds(invoices: AcombaInvoiceAr[]): string[] {
    const ids = invoices
        .filter(invoice => !invoice.isFullyPaid)
        .map(invoice => invoice.transphereId)
        .filter((id): id is string => !!id);

    return [...new Set(ids)];
}

/** Resolves one batch. Null means this batch could not be resolved at all. */
async function resolveChunk(
    bridgeUrl: string,
    ids: string[],
    reportReceiptSubscription: (active: boolean | null) => void,
): Promise<AcombaPaymentLink[] | null> {
    try {
        const result = await acombaBridgeService.resolveTransphereLinks(bridgeUrl, ids);
        if (!result.resolved) return null;

        // Company-level, so every chunk carries the same answer.
        reportReceiptSubscription(result.receiptSubscriptionActive ?? null);

        // Per-invoice misses are normal (an invoice can be transmitted yet have no link) and are dropped
        // rather than reported: the absence of a row is the "no link" state downstream.
        return result.links
            .filter(link => !!link.url)
            .map(link => ({ transphereId: link.transphereId, url: link.url as string }));
    } catch {
        // A bridge older than this feature answers 404; a site without Transphere never gets past the
        // credential check. Both are "not resolved", never "no links".
        return null;
    }
}
